export function isFootnoteDefinitionInEndnotes(targetId: string): boolean {
  if (typeof document === 'undefined') return false
  const el = document.getElementById(targetId)
  if (!el) return false
  return (
    el.getAttribute('role') === 'doc-footnote' &&
    el.closest('[role="doc-endnotes"]') != null
  )
}

export function isElementVisible(el: Element): boolean {
  if (typeof window === 'undefined') return false
  let node: Element | null = el
  while (node && node !== document.documentElement) {
    if (window.getComputedStyle(node).display === 'none') return false
    node = node.parentElement
  }
  return true
}

const noterefClickRegistry = new Map<string, Element>()
let trackingInitialized = false

export function ensureNoterefClickTracking(): void {
  if (typeof document === 'undefined' || trackingInitialized) return
  trackingInitialized = true
  document.addEventListener(
    'click',
    (e: MouseEvent) => {
      const anchor = (e.target as Element)?.closest('a[role="doc-noteref"]')
      if (!(anchor instanceof HTMLAnchorElement)) return
      if (anchor.closest('[role="doc-endnotes"]') != null) return
      const href = anchor.getAttribute('href')
      if (!href?.startsWith('#')) return
      let definitionId: string
      try {
        definitionId = decodeURIComponent(href.slice(1))
      } catch {
        return
      }
      const suffix = definitionId.startsWith('footnote-')
        ? definitionId.slice('footnote-'.length)
        : null
      if (!suffix) return
      noterefClickRegistry.set(suffix, anchor)
    },
    true
  )
}

export function getBestNoterefForFootnote(
  footnoteIdSuffix: string
): HTMLAnchorElement | null {
  if (typeof document === 'undefined') return null
  const clicked = noterefClickRegistry.get(footnoteIdSuffix)
  if (clicked instanceof HTMLAnchorElement && isElementVisible(clicked)) {
    return clicked
  }
  const definitionId = `footnote-${footnoteIdSuffix}`
  const anchors = document.querySelectorAll('a[role="doc-noteref"]')
  for (let i = 0; i < anchors.length; i++) {
    const node = anchors[i]
    if (!(node instanceof HTMLAnchorElement)) continue
    const href = node.getAttribute('href')
    if (!href?.startsWith('#')) continue
    let target: string
    try {
      target = decodeURIComponent(href.slice(1))
    } catch {
      continue
    }
    if (target !== definitionId) continue
    if (node.closest('[role="doc-endnotes"]') != null) continue
    if (!isElementVisible(node)) continue
    return node
  }
  return null
}

export function scrollToNoteref(footnoteIdSuffix: string): void {
  const el = getBestNoterefForFootnote(footnoteIdSuffix)
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  ;(el as HTMLElement).focus({ preventScroll: true })
}

export function _resetNoterefClickTrackingForTest(): void {
  noterefClickRegistry.clear()
  trackingInitialized = false
}

export function unwrapInvalidDocNoterefs(
  root: HTMLElement,
  unwrapMissingTargets: boolean
): void {
  root.querySelectorAll('a[role="doc-noteref"]').forEach((node) => {
    if (!(node instanceof HTMLAnchorElement)) return
    const href = node.getAttribute('href')
    if (!href?.startsWith('#')) return
    const targetId = decodeURIComponent(href.slice(1))
    if (!targetId) return

    if (isFootnoteDefinitionInEndnotes(targetId)) return

    const el = document.getElementById(targetId)
    if (el) {
      node.replaceWith(...Array.from(node.childNodes))
      return
    }

    if (unwrapMissingTargets) {
      node.replaceWith(...Array.from(node.childNodes))
    }
  })
}
